import React, { useCallback, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MouseIcon, PlayIcon } from "lucide-react";
import { HeroScene } from "./hero/HeroScene";
import { ReelModal } from "./hero/ReelModal";
import { useScrollVideo } from "./hero/useScrollVideo";
const badges = ["4K", "16:9", "9:16"];
interface HeroBillboardProps {
  align?: "left" | "center";
}
export function HeroBillboard({
  align = "left"
}: HeroBillboardProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const {
    progress,
    autoplay,
    toggleAutoplay,
    onLoadedMetadata
  } = useScrollVideo(sectionRef, videoRef);
  const [reelOpen, setReelOpen] = useState(false);
  const closeReel = useCallback(() => setReelOpen(false), []);
  const centered = align === "center";
  return (
    // Tall section = scroll runway; the frame stays pinned while scroll scrubs the video.
    <section ref={sectionRef} id="top" className="relative h-[250vh] w-full bg-ink" aria-label="Brandhero AI" data-id="element-150" data-mp="17ijiqfoa5c2r">
      <div className="sticky top-0 h-[100svh] min-h-[640px] w-full overflow-hidden" data-id="element-151" data-mp="2047znwx8rkfd">
        <HeroScene videoRef={videoRef} onLoadedMetadata={onLoadedMetadata} data-id="element-152" data-mp="1v5d8o7p4epk6" />
        <div className="absolute inset-0" style={{
          background: centered ? "linear-gradient(0deg, rgba(15,15,15,0.85) 0%, rgba(15,15,15,0.55) 55%, rgba(15,15,15,0.35) 100%)" : "linear-gradient(90deg, rgba(15,15,15,0.96) 0%, rgba(15,15,15,0.75) 30%, rgba(15,15,15,0) 60%)"
        }} aria-hidden="true" data-id="element-153" data-mp="296po8i0dcp5j" />
        <div className="absolute inset-0" style={{
          background: "linear-gradient(0deg, #0F0F0F 0%, rgba(15,15,15,0.6) 20%, rgba(15,15,15,0) 40%)"
        }} aria-hidden="true" data-id="element-154" data-mp="1q7bqrq3fkwaa" />

        <div className={`container-x relative flex h-full flex-col justify-end pb-36 md:pb-40 ${centered ? "items-center text-center" : ""}`} data-id="element-155" data-mp="xqsorzuwmmu2">
          <p className="mb-6 flex items-center gap-2 font-mono text-[12px] tracking-[0.12em] text-signal md:text-[13px]" data-id="element-156" data-mp="tp75nwrs1cbf">
            <span className="rec-dot is-round inline-block h-2 w-2 bg-signal" aria-hidden="true" data-id="element-157" data-mp="2xtacfhlsdyp9" />
            REC — A BRANDHERO AI STUDIO
          </p>
          <h1 className="max-w-[1000px] font-serif text-[44px] leading-[0.98] tracking-[-0.02em] text-white sm:text-[clamp(56px,6.4vw,96px)]" data-id="element-158" data-mp="32lbhojkjep5s">
            <span className="sm:block" data-id="element-159" data-mp="29vcpifb1qbww">AI films that don't </span>
            <em className={`italic text-signal ${centered ? "" : "sm:inline-block sm:pl-[1.1em]"}`} data-id="element-160" data-mp="29ww7qbg9ix1b">feel like AI.</em>
          </h1>
          <div className={`mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[12px] tracking-[0.08em] text-white/80 md:text-[13px] ${centered ? "justify-center" : ""}`} data-id="element-161" data-mp="2jtiq8tl49c06">
            <span data-id="element-162" data-mp="2oqixh7tpcksv">2026 · ADS · SERIES · MICRODRAMAS · TRAILERS · REELS</span>
            <span className="flex gap-2" data-id="element-163" data-mp="3v8607axsnegm">
              {badges.map(b => <span key={b} className="border border-white/40 px-1.5 py-0.5 text-[11px] text-white/80" data-id="element-164" data-mp="41bpeldaevmc">
                  {b}
                </span>)}
            </span>
          </div>
          <p className={`mt-5 max-w-[420px] text-[14px] leading-relaxed text-body md:text-[16px] ${centered ? "mx-auto" : ""}`} data-id="element-165" data-mp="1jmdna4ic81ik">
            Commercial films for every screen — directed by people, built to sell.
          </p>
          <div className={`mt-8 flex flex-wrap gap-3 ${centered ? "justify-center" : ""}`} data-id="element-166" data-mp="19v98b4snwgnq">
            <button type="button" onClick={() => setReelOpen(true)} aria-haspopup="dialog" className="btn-white" data-id="element-167" data-mp="14xzl7vsqwh32">
              <PlayIcon className="h-4 w-4 fill-current" aria-hidden="true" data-id="element-168" data-mp="10wxkfaj6jwyz" />
              <span className="roll inline-block" data-id="element-169" data-mp="vw9uytalno6v"><span data-id="element-170" data-mp="vxkw0lcnuhua">PLAY LAUNCH FILM</span></span>
            </button>
            <a href="#book" className="btn-graphite" data-id="element-171" data-mp="qy84hnaugmld">
              <span className="roll inline-block" data-id="element-172" data-mp="2qaarh7iofz3s"><span data-id="element-173" data-mp="2q994q5sato68">START YOUR FILM</span></span>
            </a>
          </div>
        </div>

        <div className={`absolute bottom-36 right-[var(--gutter)] hidden items-center gap-4 md:bottom-40 ${centered ? "xl:flex" : "md:flex"}`} data-id="element-174" data-mp="2lbzhmwsdtolk">
          {!autoplay && <span className="flex items-center gap-2 font-mono text-[11px] tracking-[0.12em] text-white/60" data-id="element-175" data-mp="1mxtwt8vwv621">
              <MouseIcon className="h-4 w-4" aria-hidden="true" data-id="element-176" data-mp="18yauwxtp0qy6" />
              SCROLL TO PLAY
            </span>}
          <button type="button" role="switch" onClick={toggleAutoplay} aria-checked={autoplay} aria-label="Autoplay reel" className="flex h-11 items-center gap-3 border border-white/40 bg-black/40 px-4 font-mono text-[12px] tracking-[0.12em] text-white transition-colors duration-150 hover:border-white hover:bg-white/10" data-id="element-177" data-mp="1e09vjzy1t9rf">
            AUTOPLAY
            <span className={`flex h-5 w-9 items-center border p-[2px] transition-colors duration-150 ${autoplay ? "border-signal bg-signal-tint" : "border-white/40"}`} aria-hidden="true" data-id="element-178" data-mp="gaoq5m8qbg9q">
              <span className={`h-full w-3.5 transition-[transform,background-color] duration-200 ease-out ${autoplay ? "translate-x-[16px] bg-signal" : "translate-x-0 bg-white/70"}`} data-id="element-179" data-mp="1orajxyppcds" />
            </span>
          </button>
        </div>

        {/* Scrub progress */}
        <div className="absolute inset-x-0 bottom-0 h-[2px] bg-white/10" aria-hidden="true" data-id="element-180" data-mp="6lhu4v9q6q3y">
          <motion.div className="h-full w-full origin-left bg-signal" style={{
            scaleX: progress
          }} data-id="element-181" data-mp="kkr95rm73e6c" />
        </div>
      </div>
      <ReelModal open={reelOpen} onClose={closeReel} data-id="element-182" data-mp="ucdre7wyadil" />
    </section>
  );
}